import { float, uniform } from "three/tsl";
import * as THREE from "three/webgpu";

const INKING_DEFAULT_THICKNESS_MAX = 4.0;
const INKING_DEFAULT_THICKNESS_MIN = 1.0;
const INKING_DEFAULT_VARIATION = 0.5;
const INKING_DEFAULT_DETAIL = 0.5;
const INKING_DEFAULT_SHADOW_RESPONSE = 0.75;
// Remaps the normalized visible-light range before converting it to shade.
// 0 = bias toward highlights, 0.5 = neutral, 1 = bias toward shadows.
const INKING_DEFAULT_SHADOW_EXPOSURE = 0.5;
const INKING_DEFAULT_DISTANCE_NEAR = 2.0; // world units
const INKING_DEFAULT_DISTANCE_FAR = 30.0; // world units
const INKING_DEFAULT_COLOR = "#000000";
const INKING_DEFAULT_OPACITY = 1.0;
const INKING_DEFAULT_LIGHT_RANGE_MIN = 0.18;
const INKING_DEFAULT_LIGHT_RANGE_MAX = 0.82;
const INKING_DEFAULT_CAMERA_NEAR = 0.1;
const INKING_DEFAULT_CAMERA_FAR = 1000;

export interface InkingUniforms {
  /** Stroke half-width in pixels at distanceNear. */
  thicknessMax: { value: number };
  /** Stroke half-width in pixels at distanceFar. */
  thicknessMin: { value: number };
  /** Silhouette boost [0–1] — scales thickness up at grazing angles. */
  variation: { value: number };
  /** Crease sensitivity [0–1] — how small a normal break counts as a line. */
  detail: { value: number };
  /** Shadow-driven width modulation [0–1]. */
  shadowResponse: { value: number };
  /** Bias applied after scene-relative visible-light normalization [0–1].
   *  0 = favor highlight linework, 0.5 = neutral, 1 = favor shadow linework.
   *  Drives both the line-width response and the gentle visibility fade. */
  shadowExposure: { value: number };
  /** World-unit distance at which the stroke is at full thicknessMax. */
  distanceNear: { value: number };
  /** World-unit distance at which the stroke blends to thicknessMin. */
  distanceFar: { value: number };
  /** Smoothed p10 visible-face luminance from the adaptation pass. */
  lightRangeMin: { value: number };
  /** Smoothed p90 visible-face luminance from the adaptation pass. */
  lightRangeMax: { value: number };
  r: { value: number };
  g: { value: number };
  b: { value: number };
  opacity: { value: number };
  /** Camera near plane, synced per-frame. Used to linearize scene depth. */
  cameraNear: { value: number };
  /** Camera far plane, synced per-frame. Used to linearize scene depth. */
  cameraFar: { value: number };
}

let uniforms: InkingUniforms | null = null;

export function getInkingUniforms(): InkingUniforms {
  if (uniforms) return uniforms;

  const color = new THREE.Color(INKING_DEFAULT_COLOR);
  uniforms = {
    thicknessMax: uniform(float(INKING_DEFAULT_THICKNESS_MAX)),
    thicknessMin: uniform(float(INKING_DEFAULT_THICKNESS_MIN)),
    variation: uniform(float(INKING_DEFAULT_VARIATION)),
    detail: uniform(float(INKING_DEFAULT_DETAIL)),
    shadowResponse: uniform(float(INKING_DEFAULT_SHADOW_RESPONSE)),
    shadowExposure: uniform(float(INKING_DEFAULT_SHADOW_EXPOSURE)),
    distanceNear: uniform(float(INKING_DEFAULT_DISTANCE_NEAR)),
    distanceFar: uniform(float(INKING_DEFAULT_DISTANCE_FAR)),
    lightRangeMin: uniform(float(INKING_DEFAULT_LIGHT_RANGE_MIN)),
    lightRangeMax: uniform(float(INKING_DEFAULT_LIGHT_RANGE_MAX)),
    r: uniform(float(color.r)),
    g: uniform(float(color.g)),
    b: uniform(float(color.b)),
    opacity: uniform(float(INKING_DEFAULT_OPACITY)),
    cameraNear: uniform(float(INKING_DEFAULT_CAMERA_NEAR)),
    cameraFar: uniform(float(INKING_DEFAULT_CAMERA_FAR)),
  };
  return uniforms;
}

export function syncInkingUniforms(
  thicknessMax: number,
  thicknessMin: number,
  variation: number,
  detail: number,
  shadowResponse: number,
  shadowExposure: number,
  distanceNear: number,
  distanceFar: number,
  colorHex: string,
  opacity: number,
): void {
  const u = getInkingUniforms();
  u.thicknessMax.value = thicknessMax;
  u.thicknessMin.value = thicknessMin;
  u.variation.value = variation;
  u.detail.value = detail;
  u.shadowResponse.value = shadowResponse;
  u.shadowExposure.value = shadowExposure;
  u.distanceNear.value = distanceNear;
  u.distanceFar.value = distanceFar;
  u.opacity.value = opacity;

  const color = new THREE.Color(colorHex);
  u.r.value = color.r;
  u.g.value = color.g;
  u.b.value = color.b;
}

export function syncInkingCameraPlanes(near: number, far: number): void {
  const u = getInkingUniforms();
  u.cameraNear.value = near;
  u.cameraFar.value = far;
}
